import { IconCheck, IconX } from "@tabler/icons-react";
import type { CustomDomain } from "../domain-queries";

const STEPS = ["Address added", "Record found", "Certificate issued", "Live"] as const;

/** How far an address has come, step by step: done, the step it is on, or the step that failed. */
function progress(status: CustomDomain["status"]): { done: number; failed: boolean } {
  switch (status) {
    case "pending_verification":
      return { done: 1, failed: false };
    case "verifying":
      return { done: 2, failed: false };
    case "active":
      return { done: 4, failed: false };
    case "failed":
      return { done: 1, failed: true };
    case "disabled":
      return { done: 0, failed: true };
  }
}

/**
 * The road from an address typed to an address live (studio design): four steps, each done, current or failed, in
 * words as well as marks, so the state never rests on colour.
 */
export function DomainSteps({ domain }: { readonly domain: CustomDomain }) {
  const { done, failed } = progress(domain.status);
  return (
    <ol className="ws-steps" aria-label={`Progress for ${domain.hostname}`}>
      {STEPS.map((step, i) => {
        const state = i < done ? "done" : i === done ? (failed ? "failed" : "current") : "waiting";
        return (
          <li key={step} className="ws-step" data-step={state}>
            <span className="ws-step__mark" aria-hidden>
              {state === "done" ? (
                <IconCheck size={14} stroke={3} />
              ) : state === "failed" ? (
                <IconX size={14} stroke={3} />
              ) : (
                String(i + 1)
              )}
            </span>
            <span className="ws-step__label">
              {step}
              <span className="how-visually-hidden">
                {state === "done"
                  ? ", done"
                  : state === "current"
                    ? ", in progress"
                    : state === "failed"
                      ? ", did not work"
                      : ", still to come"}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}
